import { z } from 'zod';
import {
  INVOICE_STATUSES,
  QUOTE_STATUSES,
  SALES_ORDER_STATUSES,
  SUPPORTED_DOCUMENT_CURRENCIES,
} from '../enums.js';
import {
  listFiltersQueryFragment,
  optionalDate,
  optionalNumber,
  optionalText,
  paginationQuerySchema,
  toPatchSchema,
  uuidSchema,
} from './common.js';
import type { ItemListRef } from './item-list.js';
import { currencySchema } from './organization.js';
import { businessManagerFilterSchema, type BusinessManagerRef, type RecordRef } from './records.js';

/**
 * Quotes, sales orders and invoices.
 *
 * One file because they are the same document at three points in its life, and
 * splitting them would mean maintaining three copies of a line-item shape that
 * has to agree. They keep separate tables — a quote line has a discount and no
 * fulfilment state, an order line has both — but the contract the client codes
 * against is shared.
 *
 * Totals are never accepted from the client. They are computed server-side
 * from the lines, because a total the browser sends is a total a browser can
 * get wrong, and an invoice that disagrees with its own line items is not
 * something a customer forgives.
 */

/**
 * The currencies a document can be explicitly raised in, in addition to
 * whatever an account's own free-text `currency` field already allows.
 *
 * This is a UI-facing shortlist, not a restriction: the underlying `currency`
 * column still accepts any ISO code (an account billed in EUR keeps working
 * exactly as before). It exists so a quote can be raised in USD without
 * first having to change the linked account's default currency.
 */
// The same list the "change currency" action offers. It used to be INR and USD
// only, so a quote could be switched to CAD or EUR after it was saved but not
// while it was being written.
export const DOCUMENT_CURRENCIES = SUPPORTED_DOCUMENT_CURRENCIES;
export type DocumentCurrency = (typeof DOCUMENT_CURRENCIES)[number];

const money = z.coerce.number().min(0).max(1e15);
const signedMoney = z.coerce.number().min(-1e15).max(1e15);
const rate = z.coerce.number().min(0).max(100);
/** Whole units only — half a licence or a third of a seat is not a line a business raises. */
const quantity = z.coerce.number().int('Must be a whole number').min(1).max(1_000_000);

export const lineItemSchema = z.object({
  /** Null for an ad-hoc line that is not in the catalogue. */
  productId: uuidSchema.nullish(),
  /**
   * The price-list entry the line was picked from, if any. Only a pointer for
   * display and re-pricing: the line's description, price and tax are its own
   * snapshot, so editing them never touches the list.
   */
  itemListEntryId: uuidSchema.nullish(),
  /**
   * A detailed description, long enough for a real paragraph rather than a
   * one-line label — and rendered through `formatLineDescription` (in
   * `document-html.ts`/`document-template-render.ts`), which understands a
   * small, safe markdown-like subset (`**bold**`, `*italic*`, `- ` and
   * `1. ` list lines, blank-line paragraphs) on top of preserving line
   * breaks. Still a plain string at rest: never raw HTML from a client.
   */
  description: z.string().trim().min(1, 'Required').max(4000),
  quantity: quantity.default(1),
  unitPrice: money.default(0),
  discountPercent: rate.default(0),
  taxRate: rate.default(0),
});
export type LineItemPayload = z.infer<typeof lineItemSchema>;

/** Line items are replaced wholesale, never patched individually. */
const lines = z.array(lineItemSchema).max(200);

const documentBase = {
  subject: z.string().trim().min(1, 'Required').max(300),
  terms: optionalText(8000),
  accountId: uuidSchema.nullish(),
  contactId: uuidSchema.nullish(),
  businessManagerId: uuidSchema.optional(),
  lineItems: lines.optional(),
  customFields: z.record(z.string(), z.unknown()).optional(),
};

// --- Quotes ---------------------------------------------------------------

/**
 * A quote needs an account and an expiry date.
 *
 * Both are required here rather than only in the form, because a form is a
 * convenience and this is the boundary. A quote with no account is one nobody
 * can bill from — the invoice raised from it inherits the account, so the gap
 * surfaces at the worst moment. A quote with no expiry is a price the customer
 * can hold you to indefinitely.
 *
 * `documentBase` makes `accountId` nullish for orders and invoices, which
 * inherit theirs; quotes override it.
 */
export const createQuoteSchema = z.object({
  ...documentBase,
  accountId: uuidSchema.describe('Required: a quote is addressed to somebody'),
  status: z.enum(QUOTE_STATUSES).default('DRAFT'),
  validUntil: z.iso.date('Required: say how long the price holds'),
  /** The price list the lines were picked from; each line still keeps its own price. */
  itemListId: uuidSchema.nullish(),
  /** What the quote is for. One of the organisation's own service types. */
  serviceType: z.string().trim().min(1).max(80).nullish(),
  /**
   * The PDF layout. Omitted, the organisation's default applies — and the
   * download can still override it, so this records an intent rather than a
   * constraint.
   */
  templateId: uuidSchema.nullish(),
  dealId: uuidSchema.nullish(),
  /**
   * Raise this quote in a currency other than the account's own default —
   * INR remains the default when this is left unset, and the linked
   * account's currency still applies otherwise.
   */
  currency: z.enum(DOCUMENT_CURRENCIES).optional(),
});
export type CreateQuoteInput = z.infer<typeof createQuoteSchema>;

/**
 * The patch schema keeps both non-nullable.
 *
 * `toPatchSchema` makes every field optional, which is right — a PATCH that
 * does not mention the account is not changing it. What it must not allow is
 * *clearing* one: a quote that had an account and now does not is the same
 * broken quote, arrived at more slowly.
 */
export const updateQuoteSchema = toPatchSchema(createQuoteSchema).extend({
  accountId: uuidSchema.optional(),
  validUntil: z.iso.date().optional(),
  /**
   * Redenominates the whole document — every line's price, the subtotal, tax
   * and total — into this currency, converted from the document's current
   * canonical (base-currency) value at today's rate for the new currency. Not
   * a relabelling: an INR quote changed to USD gets its numbers converted, not
   * just its symbol.
   */
  currency: currencySchema.optional(),
});
export type UpdateQuoteInput = z.infer<typeof updateQuoteSchema>;

// --- Sales orders ---------------------------------------------------------

export const createSalesOrderSchema = z.object({
  ...documentBase,
  status: z.enum(SALES_ORDER_STATUSES).default('CREATED'),
  purchaseOrder: optionalText(120),
  customerNo: optionalText(120),
  dueDate: optionalDate(),
  carrier: optionalText(120),
  adjustment: signedMoney.default(0),
  description: optionalText(8000),
  quoteId: uuidSchema.nullish(),
  dealId: uuidSchema.nullish(),
});
export type CreateSalesOrderInput = z.infer<typeof createSalesOrderSchema>;
export const updateSalesOrderSchema = toPatchSchema(createSalesOrderSchema);
export type UpdateSalesOrderInput = z.infer<typeof updateSalesOrderSchema>;

// --- Invoices -------------------------------------------------------------

export const createInvoiceSchema = z.object({
  ...documentBase,
  status: z.enum(INVOICE_STATUSES).default('DRAFT'),
  invoiceDate: optionalDate(),
  dueDate: optionalDate(),
  purchaseOrder: optionalText(120),
  /** The price list the lines were picked from; each line still keeps its own price. */
  itemListId: uuidSchema.nullish(),
  quoteId: uuidSchema.nullish(),
  dealId: uuidSchema.nullish(),
  // A blank number input submits '', which `z.coerce.number()` reads as 0.
  exciseDuty: optionalNumber(money),
  templateId: uuidSchema.nullish(),
  adjustment: signedMoney.default(0),
  description: optionalText(8000),
  salesOrderId: uuidSchema.nullish(),
});
export type CreateInvoiceInput = z.infer<typeof createInvoiceSchema>;
export const updateInvoiceSchema = toPatchSchema(createInvoiceSchema).extend({
  /** Same redenomination `updateQuoteSchema.currency` documents. */
  currency: currencySchema.optional(),
});
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceSchema>;

// --- Queries --------------------------------------------------------------

export const SALES_DOCUMENT_SORT_FIELDS = ['createdAt', 'updatedAt', 'total', 'status'] as const;

const documentQuery = paginationQuerySchema.extend({
  sortBy: z.enum(SALES_DOCUMENT_SORT_FIELDS).default('createdAt'),
  businessManagerId: businessManagerFilterSchema.optional(),
  accountId: uuidSchema.optional(),
});

export const listQuotesQuerySchema = documentQuery.extend({
  status: z.enum(QUOTE_STATUSES).optional(),
  dealId: uuidSchema.optional(),
  ...listFiltersQueryFragment,
});
export type ListQuotesQuery = z.infer<typeof listQuotesQuerySchema>;

export const listSalesOrdersQuerySchema = documentQuery.extend({
  status: z.enum(SALES_ORDER_STATUSES).optional(),
  ...listFiltersQueryFragment,
});
export type ListSalesOrdersQuery = z.infer<typeof listSalesOrdersQuerySchema>;

export const listInvoicesQuerySchema = documentQuery.extend({
  status: z.enum(INVOICE_STATUSES).optional(),
  /** Past its due date and not yet paid. */
  overdueOnly: z.stringbool().optional(),
  /** Sent, partly paid or overdue: money still to collect. */
  unpaidOnly: z.stringbool().optional(),
  ...listFiltersQueryFragment,
});
export type ListInvoicesQuery = z.infer<typeof listInvoicesQuerySchema>;

// --- Responses ------------------------------------------------------------

export interface LineItemView {
  id: string;
  product: RecordRef | null;
  description: string;
  quantity: string;
  unitPrice: string;
  discountPercent: string;
  taxRate: string;
  lineTotal: string;
  position: number;
  /** The price-list entry the line was picked from; the line's price is its own snapshot. */
  itemListEntryId: string | null;
}

/**
 * The parts every sales document shares.
 *
 * Generic over the status so each document can narrow it to its own enum
 * while still being handled by code that works on any of the three.
 */
export interface SalesDocumentSummary<TStatus extends string = string> {
  id: string;
  /** The allocated document number: Q-00007, SO-00007, INV-00007. */
  number: string;
  subject: string;
  status: TStatus;
  /** What the customer is billed in — the account's currency. */
  currency: string;
  /**
   * The money snapshot, frozen when the document was raised.
   *
   * `baseCurrency` is the organisation's own currency and `exchangeRate` is
   * how many of its units one `currency` unit bought on `exchangeRateAt`. A
   * quote sent in dollars is a promise about dollars: if the rupee moves, what
   * the customer owes does not, so these travel with the document rather than
   * being looked up when it is read.
   */
  baseCurrency: string;
  exchangeRate: string;
  exchangeRateAt: string;
  /** `ECB`, `SAME_CURRENCY`, `MANUAL` — where the rate came from. */
  exchangeRateSource: string;
  /** `total` in the organisation's own currency, at the frozen rate. */
  baseTotal: string;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  total: string;
  account: RecordRef | null;
  contact: RecordRef | null;
  businessManager: BusinessManagerRef | null;
  createdAt: string;
  updatedAt: string;
}

/** What a detail view adds. Kept separate so each document can mix it in. */
export interface SalesDocumentExtras {
  terms: string | null;
  lineItems: LineItemView[];
  customFields: Record<string, unknown>;
  /**
   * Where emailing this document would send it.
   *
   * On the detail rather than in the contact ref: a `RecordRef` is a link, and
   * this is the address a person is about to send a customer's invoice to —
   * worth showing them before they click, not after.
   */
  contactEmail: string | null;
}

export type SalesDocumentDetail<TStatus extends string = string> = SalesDocumentSummary<TStatus> &
  SalesDocumentExtras;

export interface QuoteSummary extends SalesDocumentSummary<(typeof QUOTE_STATUSES)[number]> {
  validUntil: string | null;
  /** The layout it was raised with; null means the organisation's default. */
  templateId: string | null;
  /** What the quote is for; null on one raised before the field existed. */
  serviceType: string | null;
  deal: RecordRef | null;
  /** The price list the lines were picked from, if any. */
  itemList: ItemListRef | null;
}
export interface QuoteDetail extends QuoteSummary, SalesDocumentExtras {}

export interface SalesOrderSummary extends SalesDocumentSummary<
  (typeof SALES_ORDER_STATUSES)[number]
> {
  dueDate: string | null;
  purchaseOrder: string | null;
  adjustment: string;
  quote: RecordRef | null;
  deal: RecordRef | null;
}
export interface SalesOrderDetail extends SalesOrderSummary, SalesDocumentExtras {
  customerNo: string | null;
  carrier: string | null;
  description: string | null;
}

export interface InvoiceSummary extends SalesDocumentSummary<(typeof INVOICE_STATUSES)[number]> {
  invoiceDate: string | null;
  /** The layout it was raised with; null means the organisation's default. */
  templateId: string | null;
  dueDate: string | null;
  adjustment: string;
  salesOrder: RecordRef | null;
  /** The quote this was raised from, when it came from one. */
  quote: RecordRef | null;
  deal: RecordRef | null;
  /** The price list the lines were picked from, if any. */
  itemList: ItemListRef | null;
  /** Past due and unpaid — computed, so a list can flag it without arithmetic. */
  isOverdue: boolean;
  /** Sum of the receipts against this invoice. */
  amountPaid: string;
  /** Credit-note value applied against this invoice, net of reversals. */
  amountCredited: string;
  /** `total - amountPaid - amountCredited`, floored at zero. An overpayment is not a debt. */
  balanceDue: string;
}
export interface InvoiceDetail extends InvoiceSummary, SalesDocumentExtras {
  purchaseOrder: string | null;
  exciseDuty: string | null;
  description: string | null;
}

// --- Currency conversion for forms ----------------------------------------

/**
 * Re-expresses amounts in another currency, for a form whose currency was
 * changed while it was being filled in.
 *
 * Each amount names the currency it is *anchored* in — the product's own
 * stored currency, or the one a person typed a price in — and is converted
 * from there. A line is never converted from a figure that was itself
 * converted, so INR → USD → CAD → INR ends where it started.
 */
export const convertAmountsSchema = z.object({
  to: z.enum(SUPPORTED_DOCUMENT_CURRENCIES),
  items: z
    .array(
      z.object({
        amount: z.string().trim().regex(/^-?\d+(\.\d+)?$/u, 'Not an amount'),
        from: z.enum(SUPPORTED_DOCUMENT_CURRENCIES),
      }),
    )
    .max(200),
});
export type ConvertAmountsInput = z.infer<typeof convertAmountsSchema>;

export interface ConvertAmountsResult {
  /** Converted amounts, in the order sent, to two decimal places. */
  amounts: string[];
}
